import { useCallback, useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';

/*
 * Browser phone for outgoing and incoming calls (Twilio Voice SDK).
 * Unlike useSoftphone it isn't tied to the dashboard login: the caller supplies
 * fetchToken() → { token }, heartbeat() and placeCall(number), so the P3 traveler app can use it.
 * Outgoing calls are started by the server, which rings this browser first; that call is answered automatically.
 */

const HEARTBEAT_MS = 30000;
const PLACE_WAIT_MS = 20000;

export default function useDialer({ enabled, fetchToken, heartbeat, placeCall, onEnded }) {
  const [status, setStatus] = useState('offline'); // offline | connecting | ready | error
  const [call, setCall] = useState(null); // { direction, number, label, state: calling|ringing|incoming|active }
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const [outputs, setOutputs] = useState([]);
  const [outputId, setOutputIdState] = useState('default');
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const infoRef = useRef(null);
  const startedRef = useRef(null);
  const placingRef = useRef(null);
  const endedRef = useRef(onEnded);
  endedRef.current = onEnded;

  const finish = useCallback((outcome) => {
    const info = infoRef.current;
    if (info) {
      const entry = {
        id: `${Date.now()}`,
        ...info,
        outcome: startedRef.current && outcome === 'missed' ? 'answered' : outcome,
        at: new Date(),
        duration: startedRef.current ? Math.round((Date.now() - startedRef.current) / 1000) : 0,
      };
      endedRef.current?.(entry);
    }
    clearTimeout(placingRef.current);
    placingRef.current = null;
    callRef.current = null;
    infoRef.current = null;
    startedRef.current = null;
    setCall(null);
    setMuted(false);
    setSeconds(0);
  }, []);

  const watch = useCallback((c) => {
    callRef.current = c;
    c.on('ringing', () => setCall((v) => (v ? { ...v, state: 'ringing' } : v)));
    c.on('accept', () => {
      startedRef.current = Date.now();
      setSeconds(0);
      setCall((v) => (v ? { ...v, state: 'active' } : v));
    });
    c.on('cancel', () => finish('missed'));
    c.on('reject', () => finish('declined'));
    c.on('disconnect', () => finish(startedRef.current ? 'answered' : 'noAnswer'));
    c.on('error', (e) => {
      setError(e?.message || 'Call error');
      finish('failed');
    });
  }, [finish]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let device = null;
    setStatus('connecting');
    setError('');

    (async () => {
      try {
        const { token } = await fetchToken();
        if (cancelled) return;
        device = new Device(token, { codecPreferences: ['opus', 'pcmu'], logLevel: 'error', closeProtection: true });
        deviceRef.current = device;
        device.on('registered', () => !cancelled && setStatus('ready'));
        device.on('error', (e) => {
          if (cancelled) return;
          setError(e?.message || 'Phone error');
          setStatus('error');
        });
        device.on('tokenWillExpire', async () => {
          try {
            device.updateToken((await fetchToken()).token);
          } catch {
            setError('Session expired, restart the phone.');
          }
        });
        device.on('incoming', (c) => {
          if (cancelled) return;
          if (placingRef.current) {
            clearTimeout(placingRef.current);
            placingRef.current = null;
            watch(c);
            c.accept().catch((e) => setError(e?.message || 'Could not place the call. Check microphone permission.'));
            return;
          }
          if (callRef.current || infoRef.current) return c.reject();
          infoRef.current = { direction: 'in', number: c.parameters?.From || '', label: '' };
          setCall({ ...infoRef.current, state: 'incoming' });
          watch(c);
        });
        const refreshOutputs = () => {
          if (!device.audio?.isOutputSelectionSupported) return;
          setOutputs([...device.audio.availableOutputDevices.values()].map((d) => ({ id: d.deviceId, label: d.label || d.deviceId })));
        };
        device.audio?.on?.('deviceChange', refreshOutputs);
        refreshOutputs();
        await device.register();
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Could not start the phone');
        setStatus('error');
      }
    })();

    const beat = setInterval(() => heartbeat().catch(() => {}), HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(beat);
      try {
        device?.destroy();
      } catch {
        /* already gone */
      }
      deviceRef.current = null;
      callRef.current = null;
      setStatus('offline');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (call?.state !== 'active') return undefined;
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [call?.state]);

  const dial = useCallback(async (number, label = '') => {
    if (!deviceRef.current || callRef.current || infoRef.current) return;
    setError('');
    infoRef.current = { direction: 'out', number, label };
    setCall({ ...infoRef.current, state: 'calling' });
    placingRef.current = setTimeout(() => {
      setError('The call did not connect. Try again.');
      finish('failed');
    }, PLACE_WAIT_MS);
    try {
      await placeCall(number);
    } catch (e) {
      setError(e?.message || 'Could not place the call. Check microphone permission.');
      finish('failed');
    }
  }, [finish, placeCall]);

  const answer = useCallback(async () => {
    try {
      await callRef.current?.accept();
    } catch (e) {
      setError(e?.message || 'Could not answer. Check microphone permission.');
    }
  }, []);

  const decline = useCallback(() => callRef.current?.reject(), []);
  const hangUp = useCallback(() => callRef.current?.disconnect(), []);
  const sendDigits = useCallback((d) => callRef.current?.sendDigits(d), []);
  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    setMuted((m) => {
      callRef.current.mute(!m);
      return !m;
    });
  }, []);
  const setOutput = useCallback(async (id) => {
    try {
      await deviceRef.current?.audio?.speakerDevices.set(id);
      setOutputIdState(id);
    } catch (e) {
      setError(e?.message || 'Could not switch the speaker');
    }
  }, []);

  return { status, call, muted, seconds, error, outputs, outputId, dial, answer, decline, hangUp, sendDigits, toggleMute, setOutput };
}
